import { parseEventDate } from './eventDate';
import { GPS_DISTANCE_KM, haversineKm, type StagedPhotoMeta } from './bulkImportClustering';
import type { CarouselEvent } from '../types';

// Looser than clusterPhotos' intra-batch TIME_GAP_HOURS=6, because an
// existing Event.eventDate is calendar-day precision, not a timestamp — a
// photo taken late at night could reasonably belong to the following day's
// event, or vice versa.
export const EXISTING_EVENT_DATE_TOLERANCE_DAYS = 1;

export interface SuggestedEventMatch {
  eventId: string;
  eventTitle: string;
  eventDate: string | null;
  confidence: 'date' | 'date+location';
}

const DAY_MS = 24 * 60 * 60 * 1000;

function toDayOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Suggests an existing event a bulk-import cluster might belong to, for
 * review — never auto-attaches. Only considers events with a real
 * eventDate; an undated event is never guessed at, same principle as
 * clusterPhotos not guessing at undated photos.
 */
export function findSuggestedEventMatch(
  cluster: { photos: StagedPhotoMeta[] },
  existingEvents: CarouselEvent[]
): SuggestedEventMatch | null {
  const dated = cluster.photos.filter((p): p is StagedPhotoMeta & { takenAt: Date } => p.takenAt !== null);
  if (dated.length === 0) return null;

  const times = dated.map(p => p.takenAt.getTime());
  const startDay = toDayOnly(new Date(Math.min(...times)));
  const endDay = toDayOnly(new Date(Math.max(...times)));

  const candidates = existingEvents.filter(e => {
    if (!e.eventDate) return false;
    const eventDay = toDayOnly(parseEventDate(e.eventDate));
    const daysBeforeStart = (startDay.getTime() - eventDay.getTime()) / DAY_MS;
    const daysAfterEnd = (eventDay.getTime() - endDay.getTime()) / DAY_MS;
    return daysBeforeStart <= EXISTING_EVENT_DATE_TOLERANCE_DAYS && daysAfterEnd <= EXISTING_EVENT_DATE_TOLERANCE_DAYS;
  });

  if (candidates.length === 0) return null;
  if (candidates.length === 1) {
    const e = candidates[0];
    return { eventId: e.id, eventTitle: e.title, eventDate: e.eventDate, confidence: 'date' };
  }

  // Multiple date matches — break the tie with GPS proximity when both sides have it.
  const clusterGpsPhoto = dated.find(p => p.gps);
  if (clusterGpsPhoto?.gps) {
    let closest: { event: CarouselEvent; distanceKm: number } | null = null;
    for (const e of candidates) {
      const eventGpsPhoto = e.photos.find(p => p.gpsLat != null && p.gpsLng != null);
      if (!eventGpsPhoto) continue;
      const distanceKm = haversineKm(clusterGpsPhoto.gps, { lat: eventGpsPhoto.gpsLat!, lng: eventGpsPhoto.gpsLng! });
      if (distanceKm <= GPS_DISTANCE_KM && (!closest || distanceKm < closest.distanceKm)) {
        closest = { event: e, distanceKm };
      }
    }
    if (closest) {
      return { eventId: closest.event.id, eventTitle: closest.event.title, eventDate: closest.event.eventDate, confidence: 'date+location' };
    }
  }

  // No GPS tie-break available — fall back to whichever date is closest to the cluster's midpoint.
  const midCluster = (startDay.getTime() + endDay.getTime()) / 2;
  const dateClosest = candidates.reduce<{ event: CarouselEvent; dist: number } | null>((best, e) => {
    const eventDay = toDayOnly(parseEventDate(e.eventDate!));
    const dist = Math.abs(eventDay.getTime() - midCluster);
    return !best || dist < best.dist ? { event: e, dist } : best;
  }, null);

  if (!dateClosest) return null;
  return { eventId: dateClosest.event.id, eventTitle: dateClosest.event.title, eventDate: dateClosest.event.eventDate, confidence: 'date' };
}
